import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Settings, Check, Loader2, Trash2, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function XactimateSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "", client_secret: "", username: "", instance_url: ""
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/xactimate/settings");
        setSettings(data);
        if (data.configured) {
          setForm(p => ({
            ...p, client_id: data.client_id || "", username: data.username || "",
            instance_url: data.instance_url || ""
          }));
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    if (!form.client_id || !form.client_secret) { toast.error("Client ID and Secret required"); return; }
    setSaving(true);
    try {
      await API.post("/xactimate/settings", form);
      toast.success("Estimating-software credentials saved");
      const { data } = await API.get("/xactimate/settings");
      setSettings(data);
    } catch (err) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    try {
      await API.delete("/xactimate/settings");
      toast.success("Estimating-software credentials removed");
      setSettings({ configured: false });
      setForm({ client_id: "", client_secret: "", username: "", instance_url: "" });
    } catch (err) { toast.error("Failed"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="xactimate-settings">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900">Estimating Software Integration</h1>
        <p className="text-sm text-zinc-500">Connect your company's external estimating-software account for estimate sync</p>
      </div>

      {/* Status */}
      <Card className={`border ${settings?.configured ? "border-emerald-200" : "border-amber-200"}`}>
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings?.configured ? "bg-emerald-100" : "bg-amber-100"}`}>
            <Link2 className={`w-5 h-5 ${settings?.configured ? "text-emerald-600" : "text-amber-600"}`} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-zinc-900">
              {settings?.configured ? "Connected" : "Not Connected"}
            </p>
            <p className="text-xs text-zinc-500">
              {settings?.configured
                ? `Connected on ${new Date(settings.connected_at).toLocaleDateString()}`
                : "Enter your API credentials to enable estimate sync"}
            </p>
          </div>
          <Badge className={`text-xs ${settings?.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {settings?.configured ? "Active" : "Inactive"}
          </Badge>
        </CardContent>
      </Card>

      {/* Credentials Form */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-heading font-semibold text-zinc-900">API Credentials</h3>
          <p className="text-xs text-zinc-500">
            Get your API credentials from your estimating-software provider's developer portal.
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500 font-bold">Client ID</label>
              <input value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}
                placeholder="Your Client ID"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                data-testid="xact-client-id" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-bold">Client Secret</label>
              <input type="password" value={form.client_secret}
                onChange={e => setForm(p => ({ ...p, client_secret: e.target.value }))}
                placeholder={settings?.configured ? "••••••••••" : "Your Client Secret"}
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                data-testid="xact-client-secret" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-bold">Username (Optional)</label>
              <input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                placeholder="Account username"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-bold">Instance URL (Optional)</label>
              <input value={form.instance_url} onChange={e => setForm(p => ({ ...p, instance_url: e.target.value }))}
                placeholder="https://your-instance.example.com"
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving}
              className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-sm"
              data-testid="save-xact-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Save Credentials</>}
            </Button>
            {settings?.configured && (
              <Button variant="outline" onClick={handleRemove}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="remove-xact-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border border-blue-100 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-blue-800">How it works</p>
              <ul className="text-[11px] text-blue-700 mt-1 space-y-1">
                <li>Once connected, estimates created in FieldProIQ can be synced to Xactimate</li>
                <li>Photo descriptions, inventory items, and expenses auto-populate estimate line items</li>
                <li>Estimates are reviewed and approved by management before final sync</li>
                <li>Each company uses their own Xactimate credentials — data is never shared across accounts</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
